import React from "react";
import { observer } from "mobx-react";
import { QueryPatternUpdateStore } from "../../store";
import { QueryPatternUpdateForm } from "./QueryPatternUpdateForm";
import { ErrorDialog, ConfirmCancelDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { OutdatedAfterSaveNotice } from "../../../../common/controls/forms/OutdatedAfterSaveNotice";
import { EvaluationPathType } from "../../../../api/graphql";
import { QueryPatternSource } from "../../../../api/graphql";

export type QueryPatternUpdateProps = {
  queryPatternUpdateStore: QueryPatternUpdateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const QueryPatternUpdate: React.FC<QueryPatternUpdateProps> = observer(
  ({ queryPatternUpdateStore, showToolbox = true, showSaveFooter }) => {
    return (
      <>
        <SavingOverlay open={queryPatternUpdateStore.isSaving} />
        <OutdatedAfterSaveNotice
          open={queryPatternUpdateStore.savedButOutdated}
          onReload={() => queryPatternUpdateStore.reloadQueryPattern()}
          reloadDisabled={queryPatternUpdateStore.isProcessing}
        />
        <QueryPatternUpdateForm
          queryPatternUpdateStore={queryPatternUpdateStore}
          nameError={queryPatternUpdateStore.errors.name?.message}
          onNameChange={(value: string) => {
            queryPatternUpdateStore.setNameValue(value);
          }}
          name={queryPatternUpdateStore.entityValues.name}
          patternError={queryPatternUpdateStore.errors.pattern?.message}
          onPatternChange={(value: string) => {
            queryPatternUpdateStore.setPatternValue(value);
          }}
          pattern={queryPatternUpdateStore.entityValues.pattern}
          pathTypeError={queryPatternUpdateStore.errors.pathType?.message}
          pathTypeOptions={queryPatternUpdateStore.getPathTypeOptions()}
          onPathTypeChange={(value: EvaluationPathType) => {
            queryPatternUpdateStore.setPathTypeValue(value);
          }}
          pathType={queryPatternUpdateStore.entityValues.pathType}
          variablesError={queryPatternUpdateStore.errors.variables?.message}
          onVariablesChange={(value: string) => {
            queryPatternUpdateStore.setVariablesValue(value);
          }}
          variables={queryPatternUpdateStore.entityValues.variables}
          toolTemplateError={
            queryPatternUpdateStore.errors.toolTemplate?.message
          }
          onToolTemplateChange={(value: string) => {
            queryPatternUpdateStore.setToolTemplateValue(value);
          }}
          toolTemplate={queryPatternUpdateStore.entityValues.toolTemplate}
          argsTemplateError={
            queryPatternUpdateStore.errors.argsTemplate?.message
          }
          onArgsTemplateChange={(value: string) => {
            queryPatternUpdateStore.setArgsTemplateValue(value);
          }}
          argsTemplate={queryPatternUpdateStore.entityValues.argsTemplate}
          variationsError={queryPatternUpdateStore.errors.variations?.message}
          onVariationsChange={(value: string) => {
            queryPatternUpdateStore.setVariationsValue(value);
          }}
          variations={queryPatternUpdateStore.entityValues.variations}
          exampleInstantiationError={
            queryPatternUpdateStore.errors.exampleInstantiation?.message
          }
          onExampleInstantiationChange={(value: string) => {
            queryPatternUpdateStore.setExampleInstantiationValue(value);
          }}
          exampleInstantiation={
            queryPatternUpdateStore.entityValues.exampleInstantiation
          }
          sourceError={queryPatternUpdateStore.errors.source?.message}
          sourceOptions={queryPatternUpdateStore.getSourceOptions()}
          onSourceChange={(value: QueryPatternSource) => {
            queryPatternUpdateStore.setSourceValue(value);
          }}
          source={queryPatternUpdateStore.entityValues.source}
          qualityScoreError={
            queryPatternUpdateStore.errors.qualityScore?.message
          }
          onQualityScoreChange={(value: number) => {
            queryPatternUpdateStore.setQualityScoreValue(value);
          }}
          qualityScore={queryPatternUpdateStore.entityValues.qualityScore}
          usageCountError={queryPatternUpdateStore.errors.usageCount?.message}
          onUsageCountChange={(value: number) => {
            queryPatternUpdateStore.setUsageCountValue(value);
          }}
          usageCount={queryPatternUpdateStore.entityValues.usageCount}
          isActiveError={queryPatternUpdateStore.errors.isActive?.message}
          onIsActiveChange={(value: boolean) => {
            queryPatternUpdateStore.setIsActiveValue(value);
          }}
          isActive={queryPatternUpdateStore.entityValues.isActive}
          notesError={queryPatternUpdateStore.errors.notes?.message}
          onNotesChange={(value: string) => {
            queryPatternUpdateStore.setNotesValue(value);
          }}
          notes={queryPatternUpdateStore.entityValues.notes}
          showToolbox={showToolbox}
          showSaveFooter={showSaveFooter}
          saving={queryPatternUpdateStore.saveDisabled}
          onSave={() => queryPatternUpdateStore.saveQueryPattern()}
          onCancel={() => queryPatternUpdateStore.cancelQueryPattern()}
        />
        <ErrorDialog
          open={queryPatternUpdateStore.errorDialogOpened}
          title={queryPatternUpdateStore.errorTitle}
          description={queryPatternUpdateStore.errorDescription}
          onClose={() => queryPatternUpdateStore.closeErrorDialog()}
        />
        <ConfirmCancelDialog
          open={queryPatternUpdateStore.confirmDialogOpened}
          title={queryPatternUpdateStore.confirmDialogTitle}
          description={queryPatternUpdateStore.confirmDialogDescription}
          onConfirm={() => queryPatternUpdateStore.executeConfirmedAction()}
          onCancel={() => queryPatternUpdateStore.closeConfirmDialog()}
        />
      </>
    );
  }
);
